import React, { useEffect, useState } from 'react';
import { ManagerBillingSubscription, BillingRecord, getMySubscription } from '../api.billing';
import { useLang } from '../i18n/LanguageContext';
import { subscriptionText } from '../i18n/pages/subscription';

const getToken = () => localStorage.getItem('token') || '';

const fmtCZK = (n: number | null | undefined) =>
  n != null ? `${Number(n).toLocaleString('cs-CZ')} CZK` : '—';

const monthName = (y: number, m: number, locale: string) =>
  new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

const Subscription: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { lang } = useLang();
  const t = subscriptionText[lang];
  const dateLocale = lang === 'cs' ? 'cs-CZ' : lang === 'vi' ? 'vi-VN' : lang === 'uk' ? 'uk-UA' : 'en-GB';

  const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
    PAID:    { bg: '#f0fff4', color: '#276749', label: t.statusPaid },
    PENDING: { bg: '#fffbeb', color: '#92400e', label: t.statusPending },
    OVERDUE: { bg: '#fff5f5', color: '#c53030', label: t.statusOverdue },
  };

  const [sub, setSub] = useState<ManagerBillingSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMySubscription(getToken())
      .then(setSub)
      .catch(err => setError(err?.response?.data?.message || err?.message || t.loading))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>{t.loading}</div>;
  if (error) return <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8, margin: 16 }}>{error}</div>;
  if (!sub) return null;

  const isSuspended = sub.billingStatus === 'SUSPENDED';
  const records: BillingRecord[] = sub.records || [];

  const content = (
    <>
      {/* Standalone /subscription route keeps its own title; embedded in Settings, the tab
          button already labels the section, so a second title would be redundant. */}
      {!embedded && (
        <>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>{t.pageTitle}</h1>
          <p style={{ color: '#718096', marginBottom: 20, fontSize: '0.9rem' }}>{t.subtitle}</p>
        </>
      )}

      {isSuspended && (
        <div style={{
          background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 10,
          padding: '14px 18px', marginBottom: 20, color: '#c53030',
        }}>
          <strong>{t.suspendedTitle}</strong> {t.suspendedBody}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <InfoCard label={t.cardAccountStatus} value={isSuspended ? t.cardStatusSuspended : t.cardStatusActive} color={isSuspended ? '#c53030' : '#276749'} />
        <InfoCard label={t.cardBillableApts} value={sub.activeApartmentCount ?? 0} color="#2563eb" />
        <InfoCard label={t.cardMonthlyPayment} value={fmtCZK(sub.monthlyCost)} color="#2563eb" />
        <InfoCard label={t.cardCurrency} value={sub.currency || 'CZK'} color="#4a5568" />
      </div>

      <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, color: '#1a202c', marginBottom: 12 }}>{t.pricingTitle}</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.9rem', color: '#2d3748' }}>
          <div><span style={{ color: '#718096' }}>{t.pricingMonthly}</span> <strong>{fmtCZK(sub.monthlyPricePerApartment)} {t.perAptPerMonth}</strong></div>
          <div><span style={{ color: '#718096' }}>{t.pricingAnnual}</span> <strong>{fmtCZK(sub.annualPricePerApartment)} {t.perAptPerYear}</strong></div>
        </div>
        <p style={{ marginTop: 12, fontSize: '0.82rem', color: '#718096', lineHeight: 1.5 }}>
          {t.pricingNote}
        </p>
      </section>

      {sub.apartments.length > 0 && (
        <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#1a202c', marginBottom: 12 }}>{t.yourApartmentsTitle}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sub.apartments.map(apt => (
              <div key={apt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f7fafc', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.9rem' }}>{apt.name}</span>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.82rem', color: '#718096' }}>
                  <span>{t.billedFrom} {apt.billingStartDate}</span>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, background: '#ebf4ff', color: '#2563eb' }}>
                    {fmtCZK(apt.monthlyPrice)}{t.perMonth}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
        <div style={{ fontWeight: 700, color: '#1a202c', marginBottom: 12 }}>{t.paymentHistoryTitle}</div>
        {records.length === 0
          ? <div style={{ color: '#a0aec0', textAlign: 'center', padding: '16px 0' }}>{t.noRecords}</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={th}>{t.tablePeriod}</th>
                    <th style={th}>{t.tableApts}</th>
                    <th style={th}>{t.tableTotal}</th>
                    <th style={th}>{t.tableCycle}</th>
                    <th style={th}>{t.tableStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const sc = STATUS_COLORS[r.status] || STATUS_COLORS.PENDING;
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                        <td style={td}>{monthName(r.periodYear, r.periodMonth, dateLocale)}</td>
                        <td style={td}>{r.apartmentCount}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{fmtCZK(r.totalAmount)}</td>
                        <td style={td}>{r.billingCycle === 'MONTHLY' ? t.cycleMonthly : t.cycleAnnual}</td>
                        <td style={td}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, background: sc.bg, color: sc.color }}>
                            {sc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </section>
    </>
  );

  if (embedded) return content;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      {content}
    </div>
  );
};

const InfoCard: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
    <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#4a5568', fontSize: '0.8rem' };
const td: React.CSSProperties = { padding: '10px 10px', color: '#2d3748', verticalAlign: 'middle' };

export default Subscription;
